import React, { FC, ReactElement, useEffect, useState } from "react";
import { Modal } from "@shared/components/Modal";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { getActiveAccount } from "@containers/Account/store/selectors";
import {
  ImageKey,
  PayPerLeadBuyerProfile,
  PayPerLeadBuyerFiles,
  PayPerLeadBuyerImages,
  PayPerLeadBuyerProfilePayload,
} from "@containers/PayPerLead/interface/PayPerLeadBuyerProfile";
import { Button, ErrorMessage, ImageUploader, Input } from "@shared/components";
import { FORM_ERROR_MESSAGES } from "@shared/constants";
import { emailValidator, getTrimmedValues } from "@shared/validations";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "@containers/PayPerLead/store";

import "./index.scss";

const formValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(2, `Name ${FORM_ERROR_MESSAGES.SHORT}`)
    .max(35, `Name ${FORM_ERROR_MESSAGES.LONG}`)
    .required(`This ${FORM_ERROR_MESSAGES.REQUIRED}`),
  agent_name: Yup.string()
    .trim()
    .min(2, `Full Name ${FORM_ERROR_MESSAGES.SHORT}`)
    .max(35, `Full Name ${FORM_ERROR_MESSAGES.LONG}`)
    .required(`This ${FORM_ERROR_MESSAGES.REQUIRED}`),
  agent_email: emailValidator.notRequired(),
  agent_phone: Yup.string()
    .matches(/\(\d{3}\)\s\d{3}-\d{4}/, FORM_ERROR_MESSAGES.PHONE_NUMBER)
    .optional(),
});

interface BuyerForm {
  name?: string;
  agent_name?: string;
  agent_email?: string;
  agent_phone?: string;
}

const initialValues: BuyerForm = {
  name: undefined,
  agent_name: undefined,
  agent_email: undefined,
  agent_phone: undefined,
};

export type ModalPPLBuyerProfileProps = {
  heading?: string;
  content?: ReactElement | string;
  data?: { buyerProfile: PayPerLeadBuyerProfile };
  onClose: () => void;
  onSuccess: () => void;
};

const ModalPPLBuyerProfile: FC<ModalPPLBuyerProfileProps> = ({ onClose, onSuccess, content, heading, data }) => {
  const dispatch = useDispatch();
  const [images, setImages] = useState<PayPerLeadBuyerImages>({
    [ImageKey.Logo]: null,
    [ImageKey.Photo]: null,
  });
  const [files, setFiles] = useState<PayPerLeadBuyerFiles>({
    [ImageKey.Logo]: null,
    [ImageKey.Photo]: null,
  });
  const account = useSelector(getActiveAccount());

  const { buyerProfile } = data || {};

  const [formValues, setFormValues] = useState<BuyerForm>(initialValues);

  useEffect(() => {
    if (buyerProfile) {
      setFormValues({
        name: buyerProfile.name || "",
        agent_name: buyerProfile.agent_name || "",
        agent_email: buyerProfile.agent_email || "",
        agent_phone: buyerProfile.agent_phone?.replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, "($1) $2-$3") || "",
      });

      // If buyer has images, set them as well
      setImages({
        [ImageKey.Logo]: buyerProfile.logo_url || null,
        [ImageKey.Photo]: buyerProfile.agent_photo_url || null,
      });
    }
  }, [buyerProfile]);

  const removeImage = (imageKey: ImageKey) => {
    setImages((current) => ({ ...current, [imageKey]: null }));
    setFiles((current) => ({ ...current, [imageKey]: null }));
  };

  const handleImageUpload = (previewUrl: string, file: File, key: ImageKey) => {
    setImages((current) => ({ ...current, [key]: previewUrl as string }));
    setFiles((current) => ({ ...current, [key]: file }));
  };

  const handleSubmit = (values: BuyerForm) => {
    const buyerProfile = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, value || null]),
    ) as Partial<PayPerLeadBuyerProfile>;

    dispatch(
      actions.processBuyerProfile.request({
        buyerProfile: {
          ...buyerProfile,
          agent_phone: buyerProfile.agent_phone?.replace(/[^\d]/g, ""),
          logo_url: images[ImageKey.Logo] || null,
          agent_photo_url: images[ImageKey.Photo] || null,
        },
        account_id: account?.id,
        files,
      } as PayPerLeadBuyerProfilePayload),
    );
    onSuccess();
  };

  return (
    <Modal>
      <div className="pay-per-lead-buyer-modal">
        <div className="pay-per-lead-buyer-modal-header">{heading}</div>
        <div className="pay-per-lead-buyer-modal-content">{content}</div>
        <div className="pay-per-lead-buyer-modal-form">
          <Formik
            validationSchema={formValidationSchema}
            initialValues={formValues}
            enableReinitialize
            validateOnBlur={false}
            onSubmit={async (values) => {
              try {
                // Validate and get trimmed values
                const validatedValues = await getTrimmedValues(formValidationSchema, values);

                handleSubmit(validatedValues);
              } catch (err) {
                console.error(err);
              }
            }}
          >
            {({ handleSubmit, values, isSubmitting, isValid, touched, errors }) => (
              <Form onSubmit={handleSubmit}>
                <Modal.Content>
                  <div className="form-content">
                    <div className="form-section">
                      <div className="title">Company</div>
                      <div className="section-content">
                        <label>
                          Company Name <span className="required">(Required)</span>
                        </label>
                        <Input type="text" name="name" isRequiredField value={values.name ?? undefined} />
                        <ErrorMessage isTouched={!!touched["name"]} error={errors["name"]?.toString()} />
                        <ImageUploader
                          label="Logo"
                          image={images[ImageKey.Logo] as string}
                          onRemove={() => removeImage(ImageKey.Logo)}
                          onUpload={(previewUrl: string, file: File) =>
                            handleImageUpload(previewUrl, file, ImageKey.Logo)
                          }
                        />
                      </div>
                    </div>
                    <div className="form-section">
                      <div className="title">Reach Out Person</div>
                      <div className="section-content">
                        <label>
                          Full Name<span className="required">(Required)</span>
                        </label>
                        <Input type="text" name="agent_name" value={values.agent_name ?? undefined} />
                        <ErrorMessage isTouched={!!touched["agent_name"]} error={errors["agent_name"]?.toString()} />
                        <label>Phone Number</label>
                        <div className="phone_number">
                          <Input name="agent_phone" type="phone" value={values.agent_phone?.toString()} />
                          <ErrorMessage isTouched={touched["agent_phone"]} error={errors["agent_phone"]} />
                        </div>
                        <label>Email</label>
                        <Input type="text" name="agent_email" value={values.agent_email ?? undefined} />
                        <ErrorMessage isTouched={!!touched["agent_email"]} error={errors["agent_email"]?.toString()} />
                        <ImageUploader
                          label="Photo"
                          image={images[ImageKey.Photo] as string}
                          onRemove={() => removeImage(ImageKey.Photo)}
                          onUpload={(previewUrl: string, file: File) =>
                            handleImageUpload(previewUrl, file, ImageKey.Photo)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Content>

                <Modal.Actions>
                  <div className="pay-per-lead-buyer-buttons">
                    <Button buttonStyle="blue-bordered" onClick={onClose} type="button">
                      Cancel
                    </Button>
                    <Button buttonStyle="accent-green" type="submit" disabled={isSubmitting && !isValid}>
                      Save
                    </Button>
                  </div>
                </Modal.Actions>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default ModalPPLBuyerProfile;
